import React, { useState, useEffect, useCallback } from 'react';
import { ArrowPathIcon, UserIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../../contexts/AuthContext';
import { fetchBuyerProfile, updateBuyerProfile } from '../api/marketplaceApi';

const BuyerProfile: React.FC = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    delivery_address: '',
    notification_preferences: {
      email_orders: true,
      email_invoices: true,
      email_deliveries: true,
      email_rfq: true,
    },
  });

  const loadProfile = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchBuyerProfile();
      const p = res.profile || res;
      setProfile(p);
      setForm({
        full_name: p.full_name || user?.full_name || '',
        email: p.email || user?.email || '',
        phone: p.phone || '',
        delivery_address: p.delivery_address || '',
        notification_preferences: p.notification_preferences || {
          email_orders: true,
          email_invoices: true,
          email_deliveries: true,
          email_rfq: true,
        },
      });
    } catch (err) {
      console.error('Failed to load profile:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await updateBuyerProfile({
        full_name: form.full_name,
        phone: form.phone,
        delivery_address: form.delivery_address,
        notification_preferences: form.notification_preferences,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err: any) {
      alert(err.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <ArrowPathIcon className="w-6 h-6 text-gray-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Profile & Settings</h2>
          <p className="text-sm text-gray-500 mt-0.5">Manage your personal buyer profile</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50"
        >
          {saving ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <CheckCircleIcon className="w-4 h-4" />}
          {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Changes'}
        </button>
      </div>

      {/* Personal Information */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
            <UserIcon className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Personal Information</h3>
            <p className="text-xs text-gray-500">Your buyer account details</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
            <input
              type="text"
              value={form.full_name}
              onChange={e => setForm({ ...form, full_name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={form.email}
              disabled
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-500"
            />
            <p className="text-xs text-gray-400 mt-1">Email cannot be changed here</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input
              type="tel"
              value={form.phone}
              onChange={e => setForm({ ...form, phone: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="+250 7XX XXX XXX"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Buyer Type</label>
            <input
              type="text"
              value={profile?.organisations?.length > 0 ? 'Organisation Buyer' : 'Individual Buyer'}
              disabled
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-500"
            />
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Default Delivery Address</label>
          <input
            type="text"
            value={form.delivery_address}
            onChange={e => setForm({ ...form, delivery_address: e.target.value })}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            placeholder="Delivery address for orders"
          />
        </div>
      </div>

      {/* Notification Preferences */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Notification Preferences</h3>
        <div className="space-y-3">
          {[
            { key: 'email_orders', label: 'Order updates', desc: 'Receive emails when your orders are confirmed, shipped, or delivered' },
            { key: 'email_invoices', label: 'Invoice notifications', desc: 'Receive emails when new invoices are issued' },
            { key: 'email_deliveries', label: 'Delivery alerts', desc: 'Receive emails about delivery status changes' },
            { key: 'email_rfq', label: 'RFQ & Quotation updates', desc: 'Receive emails when suppliers respond to your RFQs' },
          ].map(pref => (
            <label key={pref.key} className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer">
              <input
                type="checkbox"
                checked={form.notification_preferences[pref.key as keyof typeof form.notification_preferences]}
                onChange={e => setForm({
                  ...form,
                  notification_preferences: {
                    ...form.notification_preferences,
                    [pref.key]: e.target.checked,
                  },
                })}
                className="mt-0.5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
              />
              <div>
                <p className="text-sm font-medium text-gray-900">{pref.label}</p>
                <p className="text-xs text-gray-500">{pref.desc}</p>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Organisation Memberships */}
      {profile?.organisations && profile.organisations.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Organisation Memberships</h3>
          <div className="space-y-3">
            {profile.organisations.map((org: any) => (
              <div key={org.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-900">{org.name}</p>
                  <p className="text-xs text-gray-500">Role: {org.membership_role || 'Member'} · Status: {org.membership_status || 'Active'}</p>
                </div>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                  org.membership_status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {org.membership_status || 'Active'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BuyerProfile;
